import { Injectable } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { TrackingEvent } from '../../../shared/models/ad-banner.model';
import { AuthenticationService } from '../authentication/authentication.service';
import { AdBannersService } from './ad-banners.service';

@Injectable({ providedIn: 'root' })
export class AdBannerTrackingService {
  tracking: TrackingEvent;
  sessionId: string = uuidv4();

  constructor(
    private authenticationService: AuthenticationService,
    private adBannersService: AdBannersService
  ) {}

  setSessionId(): void {
    this.sessionId = uuidv4();
  }

  async setNewTracking(adBannerId: string): Promise<void> {
    const tracking = new TrackingEvent();
    tracking.sessionId = this.sessionId;
    tracking.customerId = this.authenticationService.getCustomerIdFromToken();
    tracking.userId = this.authenticationService.getLoggedUserFromStorage()?.id;
    tracking.adBannerId = adBannerId;
    tracking.openTimeStamp = new Date().toISOString();
    tracking.productIds = [];

    this.tracking = tracking;

    try {
      const response = await this.adBannersService
        .saveNewTrackingSession(tracking)
        .toPromise();
      this.tracking.id = response.id;
    } catch {
      // Tracking is best-effort; a failed request shouldn't break the UI flow.
    }
  }

  async bannerWasClosed(
    closedBy: 'CLOSE_BUTTON' | 'FILTER_BUTTON'
  ): Promise<void> {
    if (!this.tracking) return;
    this.tracking.closeTimeStamp = new Date().toISOString();
    this.tracking.duration = Math.abs(
      (new Date(this.tracking.openTimeStamp).getTime() -
        new Date(this.tracking.closeTimeStamp).getTime()) /
        1000
    );
    this.tracking.closedBy = closedBy;

    try {
      await this.adBannersService
        .updateTrackingSessionField(this.tracking.id, {
          closeTimeStamp: this.tracking.closeTimeStamp,
          duration: this.tracking.duration,
          closedBy: this.tracking.closedBy,
        })
        .toPromise();
    } catch {
      // Tracking is best-effort; a failed request shouldn't break the UI flow.
    }
  }

  async productWasAdded(cartId: number, productId: number): Promise<void> {
    if (!this.tracking) return;
    if (this.tracking.productIds.includes(productId)) return;

    this.tracking.productIds.push(productId);
    this.tracking.cartId = cartId;

    try {
      await this.adBannersService
        .updateTrackingSessionField(this.tracking.id, {
          productIds: this.tracking.productIds,
          cartId,
        })
        .toPromise();
    } catch {
      // Tracking is best-effort; a failed request shouldn't break the UI flow.
    }
  }
}
